/**
 * Dashboard — řídicí středisko (Command Center).
 * Hero s live stavem flotily + multi-tab widget plochy (jako Panel):
 * záložky, edit, pin, vlastní layout.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Box,
  HeartPulse,
  LayoutDashboard,
  Network,
  Server,
} from 'lucide-react';
import { api } from '../lib/api';
import { mcpCall } from '../lib/mcp';
import { IDENTITY_ME_QUERY_KEY, type IdentityMe } from '../lib/identity-me';
import { isPlatformAdmin, NODE_DIRECTORY_WIDGET_ID, seedWidgetOnce } from '../lib/node-directory';
import { Badge, ConfirmDialog, PromptDialog, StatusDot } from '../components/ui';
import { PanelTabs } from '../components/PanelTabs';
import { WidgetPanel } from '../widgets/WidgetPanel';
import {
  DEFAULT_COMMAND_CENTER,
  hydrateCommandCenter,
  loadCommandCenter,
  newCcPanelId,
  saveCommandCenter,
} from '../widgets/command-center-store';
import type { PanelsState } from '../widgets/panels-store';
import type { WidgetPanel as Panel } from '../widgets/types';
import { cn } from '../lib/utils';

export default function Dashboard() {
  const { t } = useTranslation();
  const [state, setStateRaw] = useState<PanelsState>(loadCommandCenter);
  const [editing, setEditing] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  const setState = (s: PanelsState) => {
    setStateRaw(s);
    saveCommandCenter(s);
  };

  useEffect(() => {
    let alive = true;
    hydrateCommandCenter().then((server) => {
      if (alive && server) setStateRaw(server);
    }).finally(() => {
      if (alive) setHydrated(true);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Platform admin dostane „Uzly ACM" na první plochu — jednou, až po načtení
  // uložených ploch (jinak by serverový stav seed přepsal). Odebraný se nevrací.
  const { data: me } = useQuery({
    queryKey: IDENTITY_ME_QUERY_KEY,
    queryFn: () => mcpCall<IdentityMe>('identity.me'),
    staleTime: 60_000,
    retry: false,
  });
  const platformAdmin = isPlatformAdmin(me);
  useEffect(() => {
    if (!hydrated || !platformAdmin) return;
    const next = seedWidgetOnce(state, NODE_DIRECTORY_WIDGET_ID, 'lg');
    if (next !== state) {
      setStateRaw(next);
      saveCommandCenter(next);
    }
  }, [hydrated, platformAdmin, state]);

  const { data: sysInfo } = useQuery({
    queryKey: ['dash-sysinfo'],
    queryFn: () =>
      api.mcp<Record<string, unknown>>('system-settings.system_info', {}).catch(() => ({})),
    refetchInterval: 30_000,
  });
  const { data: serverInfo } = useQuery({
    queryKey: ['server-info'],
    queryFn: () =>
      api.mcp<{ env: string; hostname: string; version: string }>('acm-self.ui_context'),
    staleTime: 60_000,
  });
  const { data: meshData } = useQuery({
    queryKey: ['mesh-nodes'],
    queryFn: () =>
      api.mcp<{
        ok: boolean;
        nodes: Array<{ node_id: string; status: string; last_seen?: string }>;
      }>('mesh.nodes', { action: 'list' }),
    refetchInterval: 30_000,
  });
  const { data: containers } = useQuery({
    queryKey: ['containers'],
    queryFn: async () => {
      const r = await api.mcp<{ containers: Array<{ state: string }> }>(
        'docker.container_list',
        {},
      ).catch(() => ({ containers: [] as Array<{ state: string }> }));
      return r.containers ?? [];
    },
    refetchInterval: 20_000,
  });
  const { data: health } = useQuery({
    queryKey: ['dash-health'],
    queryFn: () =>
      api.mcp<{ status: string; tests: Array<{ status: string }> }>(
        'toolbox.health_status',
        {},
      ).catch(() => ({ status: 'unknown', tests: [] as Array<{ status: string }> })),
    refetchInterval: 60_000,
  });

  const info = sysInfo as Record<string, unknown> | undefined;
  const hostname = String(info?.hostname ?? serverInfo?.hostname ?? 'ACM');
  const env = serverInfo?.env ?? 'dev';
  const version = serverInfo?.version ?? '…';

  const meshStats = useMemo(() => {
    const nodes = meshData?.nodes ?? [];
    const online = nodes.filter((n) => n.status === 'online' || n.status === 'ok').length;
    return { total: nodes.length, online };
  }, [meshData]);

  const runningCtr = (containers ?? []).filter((c) =>
    /running|up/i.test(String(c.state))
  ).length;
  const healthOk = (health?.tests ?? []).filter((x) =>
    x.status === 'pass' || x.status === 'ok'
  ).length;
  const healthTotal = (health?.tests ?? []).length;

  const active = state.panels.find((p) => p.id === state.activeId) ?? state.panels[0]!;

  const updatePanel = (p: Panel) =>
    setState({ ...state, panels: state.panels.map((x) => (x.id === p.id ? p : x)) });

  const addPanel = () => setAddOpen(true);

  const confirmAddPanel = (label: string) => {
    const id = newCcPanelId(state.panels);
    setState({
      activeId: id,
      panels: [...state.panels, { id, label: label.trim(), node: null, widgets: [] }],
    });
    setAddOpen(false);
    setEditing(true);
  };

  const deletePanel = (id: string) => {
    if (state.panels.length <= 1) return;
    setDeleteId(id);
  };

  const confirmDeletePanel = () => {
    if (!deleteId) return;
    const panels = state.panels.filter((p) => p.id !== deleteId);
    setState({ activeId: panels[0]!.id, panels });
    setDeleteId(null);
  };

  const resetDefault = () => setResetOpen(true);

  const confirmResetDefault = () => {
    const seed = DEFAULT_COMMAND_CENTER.panels.find((p) => p.id === active.id);
    updatePanel({
      ...active,
      widgets: seed ? structuredClone(seed.widgets) : [],
    });
    setResetOpen(false);
  };

  const deleteTargetLabel = (() => {
    const p = state.panels.find((x) => x.id === deleteId);
    if (!p) return '';
    const keys: Record<string, string> = {
      ops: 'dashboard.tabOps',
      fleet: 'dashboard.tabFleet',
      health: 'dashboard.tabHealth',
    };
    return keys[p.id] ? t(keys[p.id], p.label) : p.label;
  })();

  return (
    <div className='space-y-5'>
      {/* Command center hero */}
      <section className='cc-hero relative overflow-hidden rounded-2xl border border-gray-800/80'>
        <div className='cc-hero-glow' aria-hidden />
        <div className='relative p-5 sm:p-6 space-y-4'>
          <div className='flex flex-wrap items-start gap-4 justify-between'>
            <div className='min-w-0'>
              <div className='flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-gray-500 font-semibold'>
                <LayoutDashboard size={12} className='text-plex' />
                {t('dashboard.commandCenter', 'Řídicí středisko')}
              </div>
              <h1 className='text-2xl sm:text-3xl font-extrabold text-white tracking-tight mt-1 truncate'>
                {hostname}
              </h1>
              <p className='text-sm text-gray-400 mt-1 font-mono'>
                {env}
                {info?.os ? ` · ${String(info.os)}` : ''}
                {` · v${version}`}
              </p>
            </div>
            <div className='flex flex-wrap gap-2'>
              <Badge variant='blue'>{env}</Badge>
              <Badge variant='gray'>v{version}</Badge>
            </div>
          </div>

          <div className='grid grid-cols-2 sm:grid-cols-4 gap-2.5'>
            <HeroStat
              icon={Network}
              label={t('dashboard.statMesh', 'Mesh')}
              value={`${meshStats.online}/${meshStats.total}`}
              hint={t('dashboard.nodesOnline', 'online')}
              ok={meshStats.total === 0 || meshStats.online === meshStats.total}
            />
            <HeroStat
              icon={Box}
              label={t('dashboard.statContainers', 'Kontejnery')}
              value={String(runningCtr)}
              hint={t('dashboard.running', 'running')}
              ok={runningCtr > 0}
            />
            <HeroStat
              icon={HeartPulse}
              label={t('dashboard.statHealth', 'Health')}
              value={healthTotal ? `${healthOk}/${healthTotal}` : '—'}
              hint={health?.status ?? '—'}
              ok={!healthTotal || healthOk === healthTotal}
            />
            <HeroStat
              icon={Server}
              label={t('dashboard.statHost', 'Host')}
              value={String(info?.cpu_count ?? '—')}
              hint={t('dashboard.cpus', 'CPU')}
              ok
            />
          </div>
        </div>
      </section>

      {/* Multi-tab plochy */}
      <section className='space-y-3'>
        <div className='flex items-center gap-2'>
          <Activity size={15} className='text-plex' />
          <h2 className='text-sm font-semibold text-gray-100'>
            {t('dashboard.panelsTitle', 'Plochy střediska')}
          </h2>
          <span className='text-[11px] text-gray-600'>
            {t('dashboard.panelsHint', 'Přidej záložky, widgety, přizpůsob si view')}
          </span>
        </div>

        <PanelTabs
          state={state}
          active={active}
          editing={editing}
          onSelect={(id) => setState({ ...state, activeId: id })}
          onAdd={addPanel}
          onDelete={deletePanel}
        />

        <WidgetPanel
          panel={active}
          editing={editing}
          showTitle={false}
          onToggleEdit={() => setEditing((e) => !e)}
          onChange={updatePanel}
          onResetDefault={resetDefault}
        />
      </section>

      <PromptDialog
        open={addOpen}
        title={t('myPanel.addTabTitle', 'Nová záložka')}
        label={t('myPanel.newName', 'Název nové záložky:')}
        placeholder={t('myPanel.newNamePlaceholder', 'např. Provoz')}
        confirmLabel={t('myPanel.addTabConfirm', 'Vytvořit')}
        cancelLabel={t('common.cancel')}
        onConfirm={confirmAddPanel}
        onCancel={() => setAddOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteId}
        title={t('myPanel.delete', 'Smazat záložku')}
        description={
          deleteTargetLabel
            ? t('myPanel.deleteConfirmNamed', 'Opravdu smazat „{{name}}“?', {
              name: deleteTargetLabel,
            })
            : t('myPanel.deleteConfirm', 'Smazat tuto záložku?')
        }
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={confirmDeletePanel}
        onCancel={() => setDeleteId(null)}
      />

      <ConfirmDialog
        open={resetOpen}
        title={t('dashboard.resetTitle', 'Obnovit výchozí')}
        description={t(
          'dashboard.resetTabConfirm',
          'Obnovit výchozí rozvržení této záložky?',
        )}
        confirmLabel={t('dashboard.resetConfirmLabel', 'Obnovit')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={confirmResetDefault}
        onCancel={() => setResetOpen(false)}
      />
    </div>
  );
}

function HeroStat(props: {
  icon: typeof Network;
  label: string;
  value: string;
  hint: string;
  ok: boolean;
}) {
  const Icon = props.icon;
  return (
    <div className={cn(
      'rounded-xl border px-3 py-2.5 bg-gray-950/50 backdrop-blur-sm',
      props.ok ? 'border-gray-800/80' : 'border-amber-500/25',
    )}>
      <div className='flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold'>
        <Icon size={12} className='text-plex' />
        {props.label}
      </div>
      <div className='mt-1 flex items-end gap-2'>
        <span className='text-xl font-bold text-white tabular-nums leading-none'>
          {props.value}
        </span>
        <StatusDot status={props.ok ? 'running' : 'restarting'} />
      </div>
      <div className='text-[10px] text-gray-600 mt-1'>{props.hint}</div>
    </div>
  );
}
